import groupBy from 'lodash.groupby';
import uniqueid from 'lodash.uniqueid';
import uniqBy from 'lodash.uniqby';

import {
  OrderLine,
  DocumentsGroup,
  OrderDocumentItem,
  GroupedDocumentItem,
} from '../models';
import { OrderStateStatus } from '../constants';


export namespace DocumentsGroupUtils {
  function sortByInnerDateComparator(a: OrderDocumentItem, b: OrderDocumentItem): number {
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  }

  function sortByOuterDateComparator(a: OrderDocumentItem[], b: OrderDocumentItem[]): number {
    return new Date(b[0].createdAt).getTime() - new Date(a[0].createdAt).getTime();
  }

  function getCreditNotesVisibility(documents: DocumentsGroup[]): boolean {
    return documents.some((groupedBySeller) => groupedBySeller.creditNotes.length);
  }

  function groupByHashIterator(data: GroupedDocumentItem) {
    return data.hash || uniqueid('ui-hash_');
  }

  function addProductNameToDocuments(documents: OrderDocumentItem[], productName: string): GroupedDocumentItem[] {
    return documents.map(d => ({
      ...d, productName,
    }));
  }

  function concatDocumentsBySeller(products: OrderLine[]):
    { invoices: GroupedDocumentItem[], creditNotes: GroupedDocumentItem[] } {
    return products.reduce((acc, cur) => {
        return {
          invoices: [
            ...acc.invoices,
            ...(addProductNameToDocuments(cur.documents.invoices, cur.product.name))],
          creditNotes: [
            ...acc.creditNotes,
            ...(addProductNameToDocuments(cur.documents.creditNotes, cur.product.name))],
        };
      }, {
        invoices: [],
        creditNotes: [],
      },
    );
  }

  function groupDocumentsByHash(documents: GroupedDocumentItem[]): Array<GroupedDocumentItem[]> {
    return Object.values(groupBy(documents, groupByHashIterator))
      .map((dockArray) => uniqBy(dockArray.slice().sort(sortByInnerDateComparator), 'productName'))
      .sort(sortByOuterDateComparator);
  }

  export function mapDocuments(orderLines: OrderLine[]):
    { documents: DocumentsGroup[], isCreditNotesVisible: boolean } {

    const productsWithAllowedDocuments = orderLines.filter(({status}) => {
      return [
        OrderStateStatus.SHIPPED,
        OrderStateStatus.RETURN_DECLINED,
        OrderStateStatus.RETURN_REQUESTED,
        OrderStateStatus.RETURN_ACCEPTED,
      ].includes(status.value);
    });

    if (!productsWithAllowedDocuments.length) {
      return {
        documents: [],
        isCreditNotesVisible: false,
      };
    }

    const groupedBySeller = groupBy(productsWithAllowedDocuments, (orderLine) => orderLine.seller.shopName);

    const mappedDocuments = Object.entries(groupedBySeller)
      .map(([shopName, products]) => {
        const documentsBySeller = concatDocumentsBySeller(products);

        const filteredInvoices = Array.from(new Set(documentsBySeller.invoices.map(a => a.id)))
          .map(id => {
            return documentsBySeller.invoices.find(a => a.id === id)
          });
        const filteredProduct = filterDuplicatedOrderLines(products);

        const invoices = groupDocumentsByHash(filteredInvoices);

        const creditNotes = groupDocumentsByHash(documentsBySeller.creditNotes);

        return {
          products: {shopName, items: filteredProduct},
          invoices,
          creditNotes,
        };
      });

    return {
      documents: mappedDocuments,
      isCreditNotesVisible: getCreditNotesVisibility(mappedDocuments),
    };
  }

  export function filterDuplicatedOrderLines(orderLines: OrderLine[]): OrderLine[] {
    return Array.from(new Set(orderLines.map(a => a.product.productId)))
      .map(id => orderLines.find(a => a.product.productId === id));
  }
}
